export type SocialProofAvatar = {
  readonly id: string;
  readonly src: string;
};

export type SocialProofGender = "male" | "female";

export type SocialProofAvatarDeckParams = {
  readonly count: number;
  readonly excludeIds?: readonly string[];
  readonly seed: number;
};

export type GenderedAvatarDeckParams = {
  readonly count: number;
  readonly excludeIds?: readonly string[];
  readonly seed: number;
  readonly gender: SocialProofGender;
};

const ROOT_AVATAR_COUNT = 60;
const ROOT_STICKER_COUNT = 105;
const MALE_PHOTO_START = 100;
const MALE_PHOTO_END = 151;
const FEMALE_PHOTO_START = 200;
const FEMALE_PHOTO_END = 249;

/** Male-only photos, ids `male-photo-<n>`. Used by gendered avatar decks so
 *  the GANHOS AO VIVO ticker never pairs a male name with a female photo
 *  (legacy `lib/social-proof/wins.ts:110` parity). */
export const MALE_PHOTO_AVATARS: readonly SocialProofAvatar[] = range(
  MALE_PHOTO_START,
  MALE_PHOTO_END,
).map((index) =>
  avatar(`male-photo-${index}`, `/avatars/male/photo-${index}.webp`),
);

/** Female-only photos. See {@link MALE_PHOTO_AVATARS}. */
export const FEMALE_PHOTO_AVATARS: readonly SocialProofAvatar[] = range(
  FEMALE_PHOTO_START,
  FEMALE_PHOTO_END,
).map((index) =>
  avatar(`female-photo-${index}`, `/avatars/female/photo-${index}.webp`),
);

export const SOCIAL_PROOF_AVATARS: readonly SocialProofAvatar[] = [
  ...range(1, ROOT_STICKER_COUNT).map((index) =>
    avatar(`sticker-${index}`, `/avatars/sticker-${index}.webp`),
  ),
  ...range(1, ROOT_AVATAR_COUNT).map((index) =>
    avatar(`avatar-${index}`, `/avatars/${index}.webp`),
  ),
  ...MALE_PHOTO_AVATARS,
  ...FEMALE_PHOTO_AVATARS,
];

/** Build a deterministic local social-proof avatar deck without duplicate srcs.
 *
 * Example: `buildUniqueSocialProofAvatarDeck({ seed: 42, count: 5 })`.
 */
export function buildUniqueSocialProofAvatarDeck(
  params: SocialProofAvatarDeckParams,
): readonly SocialProofAvatar[] {
  if (params.count <= 0) return [];
  const excluded = new Set(params.excludeIds ?? []);
  const available = SOCIAL_PROOF_AVATARS.filter(
    (item) => !excluded.has(item.id),
  );
  if (params.count > available.length) {
    throw new Error(
      `social proof avatar count ${params.count} exceeds available ${available.length}`,
    );
  }
  return shuffledAvatars(available, params.seed).slice(0, params.count);
}

/** Build a deterministic deck restricted to a single gender's photo pool.
 *  Mirrors legacy `getRandomAvatarId({ gender })` so callers can guarantee
 *  the name they pair with the avatar matches its photo gender.
 *
 *  Example: `buildGenderedAvatarDeck({ seed: 7, count: 3, gender: "female" })`.
 */
export function buildGenderedAvatarDeck(
  params: GenderedAvatarDeckParams,
): readonly SocialProofAvatar[] {
  if (params.count <= 0) return [];
  const pool =
    params.gender === "male" ? MALE_PHOTO_AVATARS : FEMALE_PHOTO_AVATARS;
  const excluded = new Set(params.excludeIds ?? []);
  const available = pool.filter((item) => !excluded.has(item.id));
  if (params.count > available.length) {
    throw new Error(
      `gendered avatar count ${params.count} exceeds ${params.gender} pool ${available.length}`,
    );
  }
  return shuffledAvatars(available, params.seed).slice(0, params.count);
}

function avatar(id: string, src: string): SocialProofAvatar {
  return { id, src };
}

function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function shuffledAvatars(
  avatars: readonly SocialProofAvatar[],
  seed: number,
): readonly SocialProofAvatar[] {
  const rand = createSocialProofRandom(seed);
  const out = [...avatars];
  for (let index = out.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(rand() * (index + 1));
    [out[index], out[swapIndex]] = [out[swapIndex]!, out[index]!];
  }
  return out;
}

function createSocialProofRandom(seed: number): () => number {
  let state = (seed | 0) >>> 0;
  return function next(): number {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}
